import { Box, Typography, Skeleton } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import { getFinancialColor } from '@/theme';

export interface StatTileProps {
  label: string;
  value: string;
  subtitle?: string;
  delta?: string;
  deltaTone?: 'positive' | 'negative' | 'neutral';
  change?: number;
  changeLabel?: string;
  tone?: 'income' | 'expense' | 'profit' | 'loss' | 'neutral';
  loading?: boolean;
  autoFit?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const VALUE_SIZE = { sm: 'var(--size-lg)', md: 'var(--size-xl)', lg: 'var(--size-kpi)' } as const;
const VALUE_PX_MAX = { sm: 24, md: 32, lg: 40 } as const; // autoFit 상한(size별)
const VALUE_PX_MIN = 16;
const TONE_COLOR = {
  positive: 'var(--c-green-700)',
  negative: 'var(--c-red-600)',
  neutral: 'var(--fg-tertiary)',
} as const;

/** 값 폭 맞춤 자동 축소 — 긴 원화 숫자가 잘리지 않도록 (KPICard에서 이식) */
function AutoFitValue({ value, size, color }: { value: string; size: 'sm'|'md'|'lg'; color: string }) {
  const contRef = useRef<HTMLDivElement | null>(null);
  const measRef = useRef<HTMLDivElement | null>(null);
  const max = VALUE_PX_MAX[size];
  const [fontSize, setFontSize] = useState<number>(max);

  useLayoutEffect(() => {
    const cont = contRef.current, meas = measRef.current;
    if (!cont || !meas) return;
    const fit = () => {
      const avail = cont.clientWidth, natural = meas.scrollWidth;
      if (avail <= 0 || natural <= 0) return;
      setFontSize(natural <= avail ? max : Math.max(VALUE_PX_MIN, Math.floor((max * avail) / natural * 0.96)));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(cont);
    return () => ro.disconnect();
  }, [value, max]);

  const base = {
    fontFamily: 'var(--font-numeric)', fontWeight: 800, lineHeight: 1.15,
    letterSpacing: 'var(--tracking-tight)', fontVariantNumeric: 'tabular-nums' as const,
    whiteSpace: 'nowrap' as const,
  };
  return (
    <Box ref={contRef} sx={{ width: '100%', position: 'relative', mt: 'var(--space-1)' }}>
      <Typography ref={measRef} component="div" aria-hidden sx={{
        ...base, fontSize: max, position: 'absolute', visibility: 'hidden', pointerEvents: 'none', top: 0, left: 0,
      }}>{value}</Typography>
      <Typography className="num" component="div" sx={{ ...base, fontSize, color, transition: 'font-size var(--motion-base)' }}>
        {value}
      </Typography>
    </Box>
  );
}

/** 아이콘 없는 KPI 타일 — 크기 위계(size)로 우선순위를 전달한다 */
export function StatTile({
  label, value, subtitle, delta, deltaTone, change, changeLabel,
  tone, loading = false, autoFit = false, size = 'md',
}: StatTileProps) {
  const valueColor = tone ? getFinancialColor(tone) : 'var(--fg-strong)';

  // change → delta/deltaTone 자동 산출 (명시 delta 우선)
  let effDelta = delta;
  let effTone = deltaTone;
  if (effDelta === undefined && change !== undefined) {
    effDelta = `${change > 0 ? '+' : ''}${change}%`;
    effTone = change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral';
  }

  return (
    <Box sx={{
      p: 'var(--space-5)', borderRadius: 'var(--r-20)',
      border: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-base)',
    }}>
      <Typography sx={{ fontSize: 'var(--size-sm)', fontWeight: 500, color: 'var(--fg-tertiary)' }}>
        {label}
      </Typography>

      {loading ? (
        <Skeleton variant="text" width="60%" sx={{ fontSize: VALUE_SIZE[size], mt: 'var(--space-1)' }} />
      ) : autoFit ? (
        <AutoFitValue value={value} size={size} color={valueColor} />
      ) : (
        <Typography component="div" className="num" sx={{
          fontSize: VALUE_SIZE[size], fontWeight: 800, lineHeight: 1.15,
          letterSpacing: 'var(--tracking-tight)', color: valueColor, mt: 'var(--space-1)',
        }}>{value}</Typography>
      )}

      {subtitle && (
        <Typography sx={{ fontSize: 'var(--size-xs)', color: 'var(--fg-tertiary)', mt: 'var(--space-1)' }}>
          {subtitle}
        </Typography>
      )}

      {!loading && effDelta && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', mt: 'var(--space-1)' }}>
          <Typography component="span" className="num" sx={{
            fontSize: 'var(--size-sm)', fontWeight: 600, color: TONE_COLOR[effTone ?? 'neutral'],
          }}>{effDelta}</Typography>
          {changeLabel && (
            <Typography component="span" sx={{ fontSize: 'var(--size-xs)', color: 'var(--fg-tertiary)' }}>
              {changeLabel}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
